import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import {
  Users, Plus, LogOut, FileText, Search, CheckCircle, Clock, Download,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const PortalDashboard = () => {
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", company: "", email: "", phone: "", industry: "" });

  const fetchClients = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/portal/clients`, { withCredentials: true });
      setClients(data);
    } catch {
      toast.error("Failed to load clients");
    }
    setLoading(false);
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleImport = async () => {
    try {
      const { data } = await axios.post(`${API}/api/portal/clients/import-from-leads`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchClients();
    } catch {
      toast.error("Import failed");
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/portal/clients`, newClient, { withCredentials: true });
      toast.success("Client added");
      setNewClient({ name: "", company: "", email: "", phone: "", industry: "" });
      setShowAdd(false);
      fetchClients();
    } catch {
      toast.error("Failed to add client");
    }
  };

  const filtered = clients.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.company.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-950" data-testid="portal-dashboard">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center">
              <span className="text-white font-bold text-lg">O</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">Client Portal</h1>
              <p className="text-slate-500 text-xs">{user?.name}</p>
            </div>
          </div>
          <Button variant="ghost" onClick={logout} className="text-slate-400 hover:text-white" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="stat-total">
            <div className="text-slate-400 text-xs font-medium mb-1">Total Clients</div>
            <div className="text-2xl font-bold text-white">{clients.length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="stat-signed">
            <div className="text-slate-400 text-xs font-medium mb-1">MSA Signed</div>
            <div className="text-2xl font-bold text-green-400">{clients.filter((c) => c.msa_status === "signed").length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="stat-unsigned">
            <div className="text-slate-400 text-xs font-medium mb-1">MSA Pending</div>
            <div className="text-2xl font-bold text-amber-400">{clients.filter((c) => c.msa_status === "unsigned").length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-5" data-testid="stat-onboarding">
            <div className="text-slate-400 text-xs font-medium mb-1">From Onboarding</div>
            <div className="text-2xl font-bold text-cyan-400">{clients.filter((c) => c.source === "onboarding").length}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="client-search"
            />
          </div>
          <Button onClick={() => setShowAdd(!showAdd)} className="bg-optima-blue hover:bg-optima-blue/90" data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
          <Button onClick={handleImport} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="import-btn">
            <Download className="w-4 h-4 mr-2" /> Import from Onboarding
          </Button>
        </div>

        {/* Add Client Form */}
        {showAdd && (
          <form onSubmit={handleAddClient} className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-6 grid md:grid-cols-3 gap-4" data-testid="add-client-form">
            <Input placeholder="Full Name *" required value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-client-name" />
            <Input placeholder="Company *" required value={newClient.company} onChange={(e) => setNewClient({ ...newClient, company: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-client-company" />
            <Input placeholder="Email *" type="email" required value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="bg-slate-800 border-slate-700 text-white" data-testid="new-client-email" />
            <Input placeholder="Phone" value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Input placeholder="Industry" value={newClient.industry} onChange={(e) => setNewClient({ ...newClient, industry: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
            <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="save-client-btn">Save Client</Button>
          </form>
        )}

        {/* Client List */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="clients-table">
              <thead>
                <tr className="border-b border-slate-800 text-left">
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase">Client</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase">Company</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase">MSA Status</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase">SOWs</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase">Source</th>
                  <th className="px-6 py-4 text-xs font-medium text-slate-400 uppercase"></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">Loading...</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                    {clients.length === 0 ? "No clients yet. Add one or import from onboarding." : "No clients match your search."}
                  </td></tr>
                )}
                {!loading && filtered.length > 0 && filtered.map((client) => (
                  <tr key={client.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors" data-testid={`client-row-${client.id}`}>
                    <td className="px-6 py-4">
                      <div className="text-white font-medium">{client.name}</div>
                      <div className="text-slate-500 text-sm">{client.email}</div>
                    </td>
                    <td className="px-6 py-4 text-slate-300">{client.company}</td>
                    <td className="px-6 py-4">
                      {client.msa_status === "signed" ? (
                        <span className="inline-flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-3.5 h-3.5" /> Signed</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-400 text-sm"><Clock className="w-3.5 h-3.5" /> Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-slate-300 text-sm"><FileText className="w-3.5 h-3.5" /> {client.sow_count || 0}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${client.source === "onboarding" ? "bg-cyan-500/10 text-cyan-400" : "bg-slate-700 text-slate-400"}`}>
                        {client.source}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/portal/clients/${client.id}`} className="text-optima-blue hover:text-optima-cyan text-sm font-medium" data-testid={`view-client-${client.id}`}>
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PortalDashboard;
